import { useLayoutEffect, useRef, useState } from 'react';

import styles from './TruncatedHeaderTitle.module.scss';

interface TruncatedHeaderTitleProps {
  title: string;
  /** When false the title renders plain (no ellipsis / measurement). */
  truncate?: boolean;
}

/**
 * Header title that ellipsizes when it overflows its column. Unlike a pure-CSS `:hover`
 * reveal (which fires regardless of whether the text is actually clipped), this measures
 * `scrollWidth > clientWidth` and only then exposes the full title via the native `title`
 * tooltip — so no tooltip appears when the header already fits.
 */
export const TruncatedHeaderTitle = ({ title, truncate = false }: TruncatedHeaderTitleProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!truncate) {
      setIsTruncated(false);
      return undefined;
    }

    const el = spanRef.current;
    if (!el) return undefined;

    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title, truncate]);

  if (!truncate) return <span>{title}</span>;

  return (
    <span
      ref={spanRef}
      className={styles.titleTruncate}
      title={isTruncated ? title : undefined}
    >
      {title}
    </span>
  );
};
